/**
 * GOOGLE ADS CAMPAIGNS — /google-ads/campaigns
 *
 * Every campaign on the attached customer, whatever its channel type, in the
 * shared workspace tree (campaign → ad groups / asset groups → ads). The
 * YouTube page renders the same component filtered to video campaigns.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, Globe } from 'lucide-react';
import GoogleAdsWorkspace from '@/components/google-ads/GoogleAdsWorkspace';
import { googleAdsService } from '@/services/google-ads.service';
import { GoogleAdsShell } from '@/components/google-ads/GoogleAdsShell';

type Props = {
  /** Rendered inside a TabbedPage shell — suppress this page's own Topbar. */
  embedded?: boolean;
};

export default function GoogleAdsCampaigns({ embedded = false }: Props = {}) {
  const [config, setConfig] = useState<{ configured: boolean; live: boolean } | null>(null);
  useEffect(() => {
    googleAdsService.config().then(setConfig).catch(() => undefined);
  }, []);

  const banner = config && !config.live ? (
    <div className="mb-4 flex items-start gap-2 rounded-xl border border-warning/30 bg-warning-soft p-4 text-sm">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
      <div>
        <p className="font-medium text-text-primary">Google Ads Developer Token required</p>
        <p className="mt-1 text-xs text-text-secondary">
          Campaign management calls return PERMISSION_DENIED until <code>GOOGLE_ADS_DEVELOPER_TOKEN</code> is set.
          Apply at the{' '}
          <a href="https://ads.google.com/aw/apicenter" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-info underline">
            API Center <ExternalLink size={11} />
          </a>.
        </p>
      </div>
    </div>
  ) : null;

  const workspace = (
    <GoogleAdsWorkspace
      title="Google Ads Campaigns"
      breadcrumb="Google Ads / Campaigns"
      description="Everything running on your Google Ads account — campaigns, their ad groups and the ads inside them."
      embedded
      newCampaignRoute="/google-ads/campaigns/objective"
      emptyIcon={Globe}
      emptyHint="Pick an objective and build a campaign, ad group and ad in one flow. It starts paused so you can review it first."
      viewStorageKey="gads_campaigns_view"
      banner={banner}
    />
  );
  return embedded ? workspace : <GoogleAdsShell section="Campaigns" title="Make your next move count." description="Your campaigns, ad groups and ads. One clear view of what’s running.">{workspace}</GoogleAdsShell>;
}
